import { GetMapOfSpeakerLayoutRequest, GetMapOfSpeakerLayoutResponse } from 'types/services/hearSpace';

interface CacheEntry {
  response: GetMapOfSpeakerLayoutResponse;
  timestamp: number;
}

export const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const responseCache = new Map<string, CacheEntry>();

interface CacheKeyShape {
  dimensions: number[];
  receiverSpacing: number;
  directSoundOnly: boolean | undefined;
  speakers: Array<{
    model: string;
    location: number[];
    horAngle?: number;
    verAngle?: number;
    delay?: number;
    gain?: number;
  }>;
  materials?: unknown[];
  considerFurniture?: boolean;
  noiseLevel?: number;
}

/**
 * Generate a deterministic cache key from the essential acoustic parameters of a request.
 * Only keys on parameters that actually affect the API output.
 */
export const getCacheKey = (request: GetMapOfSpeakerLayoutRequest): string => {
  const key: CacheKeyShape = {
    dimensions: [request.DimensionX, request.DimensionY, request.DimensionZ],
    receiverSpacing: request.ReceiverSpacing,
    directSoundOnly: request.DirectSoundOnly,
    speakers: request.Speakers.map((s) => ({
      model: s.ModelName,
      location: [s.Location.x, s.Location.y, s.Location.z],
      ...(s.HorAngle !== undefined && { horAngle: s.HorAngle }),
      ...(s.VerAngle !== undefined && { verAngle: s.VerAngle }),
      ...(s.Delay !== undefined && { delay: s.Delay }),
      ...(s.TotalGain !== undefined && { gain: s.TotalGain }),
    })),
  };

  if (!request.DirectSoundOnly) {
    key.materials = request.Materials || [];
    key.considerFurniture = request.ConsiderFurnitureAndOccupants || false;
    key.noiseLevel = request.NoiseLevel || 0;
  }

  return JSON.stringify(key);
};

/**
 * Retrieve a cached response if it exists and hasn't expired.
 * Returns null on miss or expiry (expired entry is evicted).
 */
export const getCachedResponse = (key: string): GetMapOfSpeakerLayoutResponse | null => {
  const cached = responseCache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.timestamp > CACHE_TTL) {
    responseCache.delete(key);
    return null;
  }
  return cached.response;
};

/**
 * Store a response in the cache. Periodically evicts expired entries
 * to prevent unbounded growth.
 */
export const setCachedResponse = (key: string, response: GetMapOfSpeakerLayoutResponse): void => {
  responseCache.set(key, { response, timestamp: Date.now() });
  if (responseCache.size % 10 === 0) {
    evictExpiredEntries();
  }
};

const evictExpiredEntries = (): void => {
  const now = Date.now();
  for (const [key, entry] of responseCache.entries()) {
    if (now - entry.timestamp > CACHE_TTL) {
      responseCache.delete(key);
    }
  }
};

/**
 * Clear all cached responses. Returns the number of entries removed.
 */
export const clearCache = (): number => {
  const size = responseCache.size;
  responseCache.clear();
  return size;
};

/** Exposed for tests to inspect cache size without exporting the Map directly. */
export const getCacheSize = (): number => responseCache.size;
